import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface EditUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: any;
  onSuccess: () => void;
}

const AVAILABLE_ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'site_admin', label: 'Site Admin' },
  { value: 'chair', label: 'Chair' },
  { value: 'treasurer', label: 'Treasurer' },
  { value: 'secretary', label: 'Secretary' },
  { value: 'trustee', label: 'Trustee' },
  { value: 'school_liaison', label: 'School Liaison' },
  { value: 'communications', label: 'Communications' },
  { value: 'catering', label: 'Catering' },
  { value: 'moderator', label: 'Moderator' },
  { value: 'user', label: 'User' },
];

export function EditUserDialog({ open, onOpenChange, user, onSuccess }: EditUserDialogProps) {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setFullName(user.full_name || "");
      setEmail(user.email || "");
      setSelectedRoles(user.user_roles?.map((r: any) => r.role) || []);
    }
  }, [user]);

  const toggleRole = (role: string) => {
    setSelectedRoles(prev => 
      prev.includes(role) 
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Update profile
    const { error: profileError } = await supabase
      .from('profiles')
      .update({
        full_name: fullName,
        email: email
      })
      .eq('id', user.id);

    if (profileError) {
      setLoading(false);
      toast({
        title: "Error",
        description: profileError.message,
        variant: "destructive"
      });
      return;
    }

    // Get current roles
    const currentRoles = user.user_roles?.map((r: any) => r.role) || [];
    
    // Roles to add
    const rolesToAdd = selectedRoles.filter(role => !currentRoles.includes(role));
    
    // Roles to remove
    const rolesToRemove = currentRoles.filter((role: string) => !selectedRoles.includes(role));

    // Add new roles
    if (rolesToAdd.length > 0) {
      const { error: addError } = await supabase
        .from('user_roles')
        .insert(rolesToAdd.map(role => ({ user_id: user.id, role: role as any })));

      if (addError) {
        setLoading(false);
        toast({
          title: "Error",
          description: addError.message,
          variant: "destructive"
        });
        return;
      }
    }

    // Remove old roles
    if (rolesToRemove.length > 0) {
      const { error: removeError } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', user.id)
        .in('role', rolesToRemove);

      if (removeError) {
        setLoading(false);
        toast({
          title: "Error",
          description: removeError.message,
          variant: "destructive"
        });
        return;
      }
    }

    setLoading(false);
    toast({
      title: "Success",
      description: "User updated successfully"
    });
    onSuccess();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
          <DialogDescription>Update user information</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fullName">Full Name</Label>
            <Input
              id="fullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="John Smith"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="john@example.com"
            />
          </div>
          <div className="space-y-2">
            <Label>Roles</Label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              {AVAILABLE_ROLES.map(role => (
                <div key={role.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={role.value}
                    checked={selectedRoles.includes(role.value)}
                    onCheckedChange={() => toggleRole(role.value)}
                  />
                  <label
                    htmlFor={role.value}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    {role.label}
                  </label>
                </div>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
